import type { ComponentType } from 'react';
import { useSelector } from 'react-redux';

import { createSelector } from '@reduxjs/toolkit';
import { capitalize, uniqueId } from 'lodash-es';

import {
  isObjectType,
  type ObjectType,
} from '@iconik-io/iconik-api/src/types/object_types';
import { useModifierKeys } from '@iconik-io/shared-lib/src/react/hooks/use_modifier_keys';

import type { ObjectWithId } from '../../types/common';
import type { MenuItem } from '../../ui/menus/menu/types';
import {
  addObjectsType,
  addObjectType,
  formatObjectDefinitionLabel,
} from '../../utils/object_type';
import { readFieldString } from '../../utils/read_field';
import type { ContextMenuProps } from './context_menu';
import type {
  ContextMenuContainerProps,
  ContextMenuDefinition,
  ContextMenuItemDefinition,
  ContextMenuReduxActions,
  GetDefinition,
} from './types';

const withObjectType = <I extends ObjectWithId>(
  contextItems: I[],
  objectType: ObjectType | undefined,
) => {
  if (objectType) {
    return addObjectsType(contextItems, objectType);
  }
  return contextItems.flatMap((contextItem) => {
    const ownType = readFieldString(contextItem, 'object_type');
    if (!ownType || !isObjectType(ownType)) {
      return [];
    }
    return [addObjectType(contextItem, ownType)];
  });
};

const getContextMenuItemsAndActions = <I extends ObjectWithId = ObjectWithId>(
  inputMenuItems: ContextMenuItemDefinition[],
  contextItems: I[],
  objectType: ObjectType | undefined,
  nameKeyPrefix?: string,
): {
  menuItems: MenuItem[];
  actions: ContextMenuReduxActions;
  additionalActions: ContextMenuReduxActions;
} => {
  let actions: ContextMenuReduxActions = {};
  let additionalActions: ContextMenuReduxActions = {};
  const menuItems: MenuItem[] = [];

  // Every suffixed label is built from the same items, so they are typed once.
  const hasObjectTypeSuffix = inputMenuItems.some(
    ({ objectTypeSuffix }) => objectTypeSuffix,
  );
  const objectTypedItems = hasObjectTypeSuffix
    ? withObjectType(contextItems, objectType)
    : [];

  inputMenuItems.forEach(
    ({ action, additionalMenuItem, multiple, objectTypeSuffix, ...item }) => {
      if (item.separator) {
        menuItems.push(item);
        return;
      }
      if (contextItems.length > 1 && !multiple) {
        return;
      }
      const label = item.label;
      if (label === undefined) {
        return;
      }
      const prefix = nameKeyPrefix ? `${nameKeyPrefix}_` : '';
      const dataUiKey = `${prefix}${label.toUpperCase().replace(/\s+/g, '_')}`;
      const actionKey = item.name ?? `${dataUiKey}_${uniqueId()}`;
      if (action) {
        actions[actionKey] = action;
      }
      if (additionalMenuItem?.action) {
        additionalActions[actionKey] = additionalMenuItem.action;
      }

      let formattedLabel = label;
      if (objectTypeSuffix) {
        formattedLabel = formatObjectDefinitionLabel(
          objectTypedItems,
          ({ objectName }) => ({
            single: `${item.label} ${capitalize(objectName)}`,
            multiple: `${item.label} ${capitalize(objectName)}`,
          }),
        );
      }
      const option: MenuItem = {
        ...item,
        name: actionKey,
        label: formattedLabel,
        dataUi: dataUiKey,
        additionalMenuItem: additionalMenuItem
          ? {
              iconName: additionalMenuItem.iconName,
              autoClose: additionalMenuItem.autoClose,
            }
          : undefined,
      };
      if (item.subitems) {
        const subitems = getContextMenuItemsAndActions(
          item.subitems,
          contextItems,
          objectType,
          actionKey,
        );
        option.subitems = subitems.menuItems;
        actions = {
          ...actions,
          ...subitems.actions,
        };
        additionalActions = {
          ...additionalActions,
          ...subitems.additionalActions,
        };
      }
      menuItems.push(option);
    },
  );
  return { menuItems, actions, additionalActions };
};

// This selector is used to memoize the definition of the context menu.
// Fixes Redux warning about re-rendering.
// See https://linear.app/iconik/issue/FE-1910
const selectDefinition = createSelector(
  [
    (state) => state,
    (_state, props) => props,
    (_state, _props, modifierKeys) => modifierKeys,
    (_state, props, _modifierKeys, getDefinition) =>
      props.getDefinition ?? getDefinition,
  ],
  (state, props, modifierKeys, getDefinition) =>
    getDefinition({ ...props, modifierKeys }, state),
);

/**
 * This function uses connectAdvancedMenu to capture context menu props generic parameters from a
 * getDefinition implementation and produce a component with a generic ContextMenuContainerProps.
 * Then this component is used in connectContextMenuConsumer to create a component with generic props also
 * to use it in other places like SelectableItems.
 *
 * @see connectContextMenuConsumer
 * @see SelectableItems
 */
const contextMenuConnector =
  <Item extends ObjectWithId, State, Options>(
    getDefinition: GetDefinition<Item, State, Options>,
  ) =>
  (contextMenuComponent: ComponentType<ContextMenuProps>) => {
    return function WrapperComponent(
      props: ContextMenuContainerProps<Item, State, Options>,
    ) {
      const MenuComponent = contextMenuComponent;

      const modifierKeys = useModifierKeys();

      const definition = useSelector<State, ContextMenuDefinition>((state) =>
        selectDefinition(state, props, modifierKeys, getDefinition),
      );

      const contextItems = props.contextItems;

      const { menuItems, actions, additionalActions } =
        getContextMenuItemsAndActions(
          definition.items,
          contextItems,
          definition.objectType,
        );

      return (
        <MenuComponent
          menuItems={menuItems}
          actions={actions}
          additionalActions={additionalActions}
          contextItemsAmount={contextItems.length}
          showIcons={props.showIcons}
          onMenuItemClick={props.onMenuItemClick}
          onAdditionalMenuItemClick={props.onAdditionalMenuItemClick}
          helpMessage={definition.helpMessage}
          useFixedWidthForSubItems={definition.useFixedWidthForSubItems}
        />
      );
    };
  };

export default contextMenuConnector;
